<template>
  <div class="top-customers-chart">
    <div v-if="loading" class="chart-loading">
      <el-icon class="is-loading"><i class="el-icon-loading"></i></el-icon>
      <span>数据加载中...</span>
    </div>
    <div v-else-if="!hasData" class="chart-empty">
      <i class="el-icon-s-data"></i>
      <span>暂无数据</span>
    </div>
    <div v-else ref="chart" class="chart-container"></div>
  </div>
</template>

<script>
import echarts from 'echarts'

export default {
  name: 'top-customers-chart',
  props: {
    data: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      chart: null,
      resizeHandler: null
    }
  },

  computed: {
    hasData() {
      return this.data && this.data.length > 0
    },

    chartData() {
      if (!this.hasData) return { names: [], times: [], fees: [] }

      const sortedData = [...this.data]
        .sort((a, b) => (b.totalTimes || 0) - (a.totalTimes || 0))
        .slice(0, 10)

      return {
        names: sortedData.map(item => item.customerName || '未知客户'),
        times: sortedData.map(item => item.totalTimes || 0),
        fees: sortedData.map(item => item.totalFee || 0)
      }
    }
  },

  watch: {
    data: {
      handler() {
        this.$nextTick(() => {
          if (this.hasData && !this.chart) {
            this.initChart()
          } else if (this.chart) {
            this.renderChart()
          }
        })
      },
      deep: true
    },

    loading(newVal) {
      if (!newVal && this.hasData) {
        this.$nextTick(() => {
          if (!this.chart) {
            this.initChart()
          } else {
            this.renderChart()
          }
        })
      }
    }
  },

  mounted() {
    this.initChart()
    this.bindEvents()
  },

  beforeDestroy() {
    this.unbindEvents()
    if (this.chart) {
      this.chart.dispose()
      this.chart = null
    }
  },

  methods: {
    /**
     * 初始化图表
     */
    initChart() {
      if (!this.$refs.chart) return

      this.chart = echarts.init(this.$refs.chart)
      this.renderChart()
    },

    /**
     * 渲染图表
     */
    renderChart() {
      console.log('客户图表渲染开始:', {
        hasChart: !!this.chart,
        hasData: this.hasData,
        dataLength: this.data ? this.data.length : 0,
        chartData: this.chartData
      })

      if (!this.chart || !this.hasData) {
        console.log('客户图表渲染终止:', { hasChart: !!this.chart, hasData: this.hasData })
        return
      }

      const { names, times, fees } = this.chartData

      const option = {
        title: {
          text: 'Top10 客户使用排行',
          left: 'center',
          top: 20,
          textStyle: {
            fontSize: 16,
            color: '#333'
          }
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          formatter: (params) => {
            const dataIndex = params[0].dataIndex
            const customerName = names[dataIndex]
            const timesValue = times[dataIndex]
            const feeValue = fees[dataIndex]

            return `
              <div>
                <strong>${customerName}</strong><br/>
                调用次数: <span style="color: #409EFF">${this.formatNumber(timesValue)}</span><br/>
                费用: <span style="color: #E6A23C">¥${this.formatMoney(feeValue)}</span>
              </div>
            `
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '15%',
          top: '15%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: names,
          axisLabel: {
            rotate: 45,
            interval: 0,
            fontSize: 10,
            color: '#666',
            formatter: (value) => {
              // 截断过长的客户名称
              return value.length > 6 ? value.substring(0, 6) + '...' : value
            }
          },
          axisTick: {
            alignWithLabel: true
          },
          axisLine: {
            lineStyle: {
              color: '#E4E7ED'
            }
          }
        },
        yAxis: {
          type: 'value',
          name: '调用次数',
          nameTextStyle: {
            color: '#666',
            fontSize: 12
          },
          axisLabel: {
            color: '#666',
            formatter: (value) => {
              if (value >= 1000000) {
                return (value / 1000000).toFixed(1) + 'M'
              } else if (value >= 1000) {
                return (value / 1000).toFixed(1) + 'K'
              }
              return value.toString()
            }
          },
          axisLine: {
            show: false
          },
          axisTick: {
            show: false
          },
          splitLine: {
            lineStyle: {
              color: '#E4E7ED',
              type: 'dashed'
            }
          }
        },
        series: [
          {
            name: '调用次数',
            type: 'bar',
            data: times,
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: '#667eea' },
                { offset: 1, color: '#764ba2' }
              ]),
              borderRadius: [6, 6, 0, 0],
              shadowColor: 'rgba(102, 126, 234, 0.3)',
              shadowBlur: 10
            },
            emphasis: {
              itemStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                  { offset: 0, color: '#7e8ff0' },
                  { offset: 1, color: '#8d5bb2' }
                ]),
                shadowColor: 'rgba(102, 126, 234, 0.5)',
                shadowBlur: 20
              }
            },
            animationDelay: function (idx) {
              return idx * 100
            }
          }
        ],
        animationEasing: 'elasticOut',
        animationDelayUpdate: function (idx) {
          return idx * 50
        }
      }

      this.chart.setOption(option, true)
    },

    /**
     * 绑定事件
     */
    bindEvents() {
      this.resizeHandler = () => {
        if (this.chart) {
          this.chart.resize()
        }
      }
      window.addEventListener('resize', this.resizeHandler)
    },

    /**
     * 解绑事件
     */
    unbindEvents() {
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler)
        this.resizeHandler = null
      }
    },

    /**
     * 格式化数字
     */
    formatNumber(num) {
      if (!num) return '0'
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },

    /**
     * 格式化金额
     */
    formatMoney(amount) {
      if (!amount) return '0.00'
      return Number(amount).toFixed(2)
    }
  }
}
</script>

<style scoped>
.top-customers-chart {
  height: 100%;
  position: relative;
  background: linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%);
  border-radius: 12px;
  padding: 8px;
}

.chart-container {
  width: 100%;
  height: 100%;
  min-height: 350px;
  animation: chartSlideUp 0.8s ease-out;
}

@keyframes chartSlideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.chart-loading,
.chart-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 320px;
  color: #909399;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.8) 0%, rgba(250, 250, 250, 0.9) 100%);
  border-radius: 8px;
}

.chart-loading {
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}

.chart-loading .el-icon {
  font-size: 32px;
  margin-bottom: 12px;
  color: #667eea;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.chart-empty i {
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.3;
  color: #667eea;
  animation: bounce 2s ease-in-out infinite;
}

@keyframes bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

.chart-empty span {
  font-size: 15px;
  color: #909399;
  font-weight: 500;
}
</style>



// WEBPACK FOOTER //
// src/custom/components/stats/message-stats/components/top-customers-chart.vue